import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAccountSectionUnlocking } from "features/Account/utils/useAccountSectionChange";
import { useAdminPanelStore } from "features/AdminPanel/store/useAdminPanelStore";
import { getSectionStates } from "features/AdminPanel/modules/utils/AdminPanel.utils";
import {
  DEBOUNCE_DELAY,
  PAYMENT_OPTION_KEYS,
} from "../../../../../../../ManageOfferings/modules/OfferingPaymentOptions/constants/OfferingPaymentOptions.constants";
import { UPDATE_ADMIN_PANEL_SETTINGS_LISITNG_LEVEL } from "../../../../../../../AdminPanel/constants/AdminPanel.api";
import useDebounce from "hooks/useDebounce";

export const useCommonToggleSectionSwitch = ({ subSectionKey }) => {
  const sectionConfig = useAdminPanelStore((state) => state.sectionConfig);
  const subSectionConfig = sectionConfig[subSectionKey] || {};
  const { value, status } = subSectionConfig;

  const { disableSwitch } = getSectionStates({ status });
  const { listing_uuid } = useParams();
  const { handleSectionChange } = useAccountSectionUnlocking();

  const isEMI =
    subSectionKey === PAYMENT_OPTION_KEYS.EMI ||
    subSectionKey === PAYMENT_OPTION_KEYS.NO_COST_EMI;

  const getInitialToggle = () => {
    if (isEMI) return value?.is_enabled ?? false;
    return !!value;
  };

  const [isToggleOn, setIsToggleOn] = useState(getInitialToggle);
  const isInitialMount = useRef(true);

  const [showDurations, setShowDurations] = useState(
    isEMI ? value?.is_enabled ?? false : false
  );

  const {
    value: selectedTenure,
    setValue: setSelectedTenure,
    debouncedValue: debouncedTenure,
  } = useDebounce({
    inititalValue: value?.tenure ?? [],
    delay: DEBOUNCE_DELAY,
  });

  useEffect(() => {
    if (isEMI) {
      setIsToggleOn(value?.is_enabled ?? false);
      setShowDurations(value?.is_enabled ?? false);
      setSelectedTenure(value?.tenure ?? []);
    } else {
      setIsToggleOn(!!value);
    }
  }, [value]);

  const callApi = (featureValue) => {
    return handleSectionChange({
      ...(listing_uuid && {
        endPoint: UPDATE_ADMIN_PANEL_SETTINGS_LISITNG_LEVEL,
      }),
      payload: {
        feature_key: subSectionKey,
        feature_value: featureValue,
        ...(listing_uuid && { listing_uuid }),
      },
    });
  };

  const handleToggle = async () => {
    const nextState = !isToggleOn;

    if (!isEMI) {
      await callApi(nextState);
      return;
    }

    setIsToggleOn(nextState);
    setShowDurations(nextState);

    if (!nextState) {
      callApi({
        is_enabled: false,
        tenure: selectedTenure,
      });
      return;
    }

    callApi({
      is_enabled: true,
      tenure: selectedTenure,
    });
  };

  useEffect(() => {
    if (!isEMI) return;
    if (!isToggleOn) return;
    if (debouncedTenure === null || debouncedTenure === undefined) return;

    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    callApi({
      is_enabled: true,
      tenure: debouncedTenure,
    });
  }, [debouncedTenure]);

  return {
    isEMI,
    isToggleOn,
    showDurations,
    disableSwitch,
    selectedTenure,
    setSelectedTenure,
    handleToggle,
  };
};
